import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories, type Article, type Category } from '@/mocks/forum';

function ArticleSchema({ article, category, relatedArticles }: { article: Article; category: Category | undefined; relatedArticles: Article[] }) {
  const origin = window.location.origin + __BASE_PATH__;
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '爱情岛论坛',
        url: origin,
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        url: `${origin}/article/${article.id}`,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.publishDate,
        image: article.coverImage,
        articleBody: article.excerpt,
        publisher: { '@type': 'Organization', name: '爱情岛论坛' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: origin },
          ...(category ? [{ '@type': 'ListItem' as const, position: 2, name: category.name, item: `${origin}/category/${category.slug}` }] : []),
          { '@type': 'ListItem', position: category ? 3 : 2, name: article.title, item: `${origin}/article/${article.id}` },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: relatedArticles.slice(0, 3).map((a, i) => ({
          '@type': 'Question' as const,
          name: a.title,
          acceptedAnswer: {
            '@type': 'Answer' as const,
            text: a.excerpt,
          },
        })),
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  const [article, setArticle] = useState<Article | null>(null);
  const [category, setCategory] = useState<Category | undefined>(undefined);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (id) {
      const found = articles.find((a) => a.id === parseInt(id));
      if (found) {
        setArticle(found);
        const cat = categories.find((c) => c.id === found.categoryId);
        setCategory(cat);
        setRelatedArticles(
          articles
            .filter((a) => a.categoryId === found.categoryId && a.id !== found.id)
            .slice(0, 3)
        );
      }
    }
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto rounded-full bg-background-100 mb-4">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-800 mb-2">文章不存在</h2>
            <p className="text-sm text-foreground-500 mb-6">未找到对应的文章，可能已被删除</p>
            <Link to="/" className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <ArticleSchema article={article} category={category} relatedArticles={relatedArticles} />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <article className="pt-20">
          <div className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
              title={article.title}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-foreground-950/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="max-w-3xl mx-auto">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full text-xs bg-background-50/20 text-background-50 backdrop-blur-sm">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 leading-tight">
                  {article.title}
                </h1>
              </div>
            </div>
          </div>

          <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full overflow-hidden bg-background-200">
                  <img src={article.avatar} alt={article.author} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-950">{article.author}</p>
                  <p className="text-xs text-foreground-500">{article.publishDate} 发布</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1"><i className="ri-eye-line"></i> {article.views.toLocaleString()}</span>
                <span className="flex items-center gap-1"><i className="ri-heart-line"></i> {article.likes.toLocaleString()}</span>
                <span className="flex items-center gap-1"><i className="ri-chat-3-line"></i> {article.comments}</span>
              </div>
            </div>

            <Breadcrumb items={[
              { label: '首页', href: '/' },
              ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
              { label: article.title },
            ]} />

            <div
              className="prose prose-lg max-w-none mt-8 text-foreground-800 leading-relaxed
                prose-h2:text-xl prose-h2:font-heading prose-h2:font-bold prose-h2:text-foreground-950 prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-base prose-p:leading-loose prose-p:mb-4 prose-p:text-foreground-700
                prose-strong:text-foreground-950 prose-strong:font-semibold"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1.5 rounded-full text-xs bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-50 text-primary-600 hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap text-sm">
                  <i className="ri-heart-line"></i> 点赞 ({article.likes.toLocaleString()})
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap text-sm">
                  <i className="ri-bookmark-line"></i> 收藏
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap text-sm">
                  <i className="ri-share-forward-line"></i> 分享
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full bg-background-100 py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}